import { approveFieldReport, rejectFieldReport, bulkApproveFieldReports, bulkRejectFieldReports, updateFieldReport } from '../services/projectService'
import { TEMPLATE_FIELD_CONFIGS } from '../config/projects'

/**
 * Custom hook for fuel log (field report) operations
 * @param {Object} params - Hook parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.templateId - Template ID for fuel logs
 * @param {Function} params.onRefresh - Callback to refresh data after operations
 * @param {Function} params.onError - Callback for error handling
 * @param {Function} params.onSuccess - Callback for success handling
 * @param {Function} params.setRowLoading - Callback to set row loading state
 * @param {Function} params.setRowError - Callback to set row error state
 * @param {Function} params.setRowSuccess - Callback to set row success state
 * @param {Function} params.setRowAction - Callback to set row action state
 * @param {Function} params.setLoading - Callback to set global loading state
 * @returns {Object} Hook methods
 */
export const useFuelLogOperations = ({
  projectId,
  templateId,
  onRefresh,
  onError,
  onSuccess,
  setRowLoading,
  setRowError,
  setRowSuccess,
  setRowAction,
  setLoading,
  templateFieldDefs  // Add this parameter to get field type information
}) => {
  /**
   * Extract error details from error response
   */
  const extractErrorDetails = (err) => {
    const errorData = err.response?.data

    const errorCode = errorData?.accError?.errorCode ||
                     errorData?.errorCode ||
                     errorData?.code ||
                     errorData?.error?.code ||
                     err.code ||
                     err.status ||
                     err.response?.status

    const errorMsg = errorData?.message ||
                    errorData?.accError?.detail ||
                    errorData?.accError?.title ||
                    errorData?.error?.message ||
                    errorData?.detail ||
                    err.message ||
                    'Operation failed'

    let displayMsg = errorMsg
    if (errorCode) {
      displayMsg = `${errorMsg} (${errorCode})`
    }

    return { errorCode, errorMsg, displayMsg }
  }

  /**
   * Approve a single fuel log report
   */
  const approveFuelLog = async (report) => {
    const id = report.id
    setRowError(prev => ({ ...prev, [id]: null }))
    setRowSuccess(prev => ({ ...prev, [id]: null }))
    setRowLoading(prev => ({ ...prev, [id]: true }))
    setRowAction(prev => ({ ...prev, [id]: 'approve' }))

    const reportTemplateId = report.formTemplateId || report.templateId || templateId || import.meta.env.VITE_FUEL_LOG_TEMPLATE_ID

    try {
      if (!reportTemplateId) {
        throw new Error('Missing templateId for approval')
      }

      await approveFieldReport(projectId, id, reportTemplateId, {
        signature: 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmci...'
        // Note: approvalStatusFieldId removed - not all templates have approval tracking fields
      })

      setRowSuccess(prev => ({ ...prev, [id]: 'Approved' }))
      onSuccess(`Form ${report.formNum || id} approved successfully.`)

      // Refresh the list
      setTimeout(() => {
        onRefresh()
      }, 1500)

    } catch (err) {
      const { displayMsg } = extractErrorDetails(err)
      setRowError(prev => ({ ...prev, [id]: displayMsg }))
      onError(displayMsg)
    } finally {
      setRowLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  /**
   * Reject a single fuel log report
   */
  const rejectFuelLog = async (report) => {
    const id = report.id
    const reason = window.prompt('Enter rejection reason:', '')

    if (reason === null) return // User cancelled

    if (!reason || reason.trim() === '') {
      alert('Rejection reason is required')
      return
    }

    const confirmed = window.confirm(`Are you sure you want to reject form ${report.formNum || id} with reason: "${reason}"?`)
    if (!confirmed) return

    setRowError(prev => ({ ...prev, [id]: null }))
    setRowSuccess(prev => ({ ...prev, [id]: null }))
    setRowLoading(prev => ({ ...prev, [id]: true }))
    setRowAction(prev => ({ ...prev, [id]: 'reject' }))

    const reportTemplateId = report.formTemplateId || report.templateId || templateId || import.meta.env.VITE_FUEL_LOG_TEMPLATE_ID

    try {
      if (!reportTemplateId) {
        throw new Error('Missing templateId for rejection')
      }

      await rejectFieldReport(projectId, id, reportTemplateId, {
        reason: reason.trim()
      })

      setRowSuccess(prev => ({ ...prev, [id]: 'Rejected' }))
      onSuccess(`Form ${report.formNum || id} rejected successfully.`, 'reject')

      // Refresh the list
      setTimeout(() => {
        onRefresh()
      }, 1500)

    } catch (err) {
      const { displayMsg } = extractErrorDetails(err)
      setRowError(prev => ({ ...prev, [id]: displayMsg }))
      onError(displayMsg)
    } finally {
      setRowLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  /**
   * Bulk approve fuel log reports
   * @param {Array} selectedReports - Array of report objects (not just IDs)
   */
  const bulkApproveFuelLogs = async (selectedReports) => {
    const comment = window.prompt('Enter approval comment (optional):', '')
    if (comment === null) return // User cancelled

    setLoading(true)

    try {
      // Group reports by template ID
      const reportsByTemplate = {}
      selectedReports.forEach(report => {
        const reportTemplateId = report.formTemplateId || report.templateId || report._templateId || templateId || import.meta.env.VITE_FUEL_LOG_TEMPLATE_ID
        if (!reportsByTemplate[reportTemplateId]) {
          reportsByTemplate[reportTemplateId] = []
        }
        reportsByTemplate[reportTemplateId].push(report.id)
      })

      // Process each template group
      let totalSuccess = 0
      let totalFailure = 0

      for (const [reportTemplateId, reportIds] of Object.entries(reportsByTemplate)) {
        if (!reportTemplateId) {
          console.error('Missing template ID for reports:', reportIds)
          totalFailure += reportIds.length
          continue
        }

        try {
          const result = await bulkApproveFieldReports(
            projectId,
            reportIds,
            reportTemplateId,
            {
              signature: 'PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmci...'
              // Note: approvalStatusFieldId removed - not all templates have approval tracking fields
            }
          )

          const successCount = result.successCount ?? (result.results ? result.results.filter(r => r.success).length : 0)
          const failureCount = result.failureCount ?? (result.results ? result.results.filter(r => !r.success).length : 0)
          totalSuccess += successCount
          totalFailure += failureCount
        } catch (err) {
          console.error(`Bulk approve failed for template ${reportTemplateId}:`, err)
          totalFailure += reportIds.length
        }
      }

      onSuccess(`Bulk approval finished. Success: ${totalSuccess} — Failed: ${totalFailure}`)

      // Refresh the list if any succeeded
      if (totalSuccess > 0) {
        setTimeout(() => {
          onRefresh()
        }, 1500)
      }

    } catch (err) {
      const { errorCode, errorMsg } = extractErrorDetails(err)
      let displayMsg = `Bulk approval failed: ${errorMsg}`
      if (errorCode) {
        displayMsg = `${displayMsg} (${errorCode})`
      }
      onError(displayMsg)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Bulk reject fuel log reports
   * @param {Array} selectedReports - Array of report objects (not just IDs)
   */
  const bulkRejectFuelLogs = async (selectedReports) => {
    const reason = window.prompt('Enter rejection reason:', '')
    if (reason === null) return // User cancelled

    if (!reason || reason.trim() === '') {
      alert('Rejection reason is required')
      return
    }

    const confirmed = window.confirm(`Are you sure you want to reject ${selectedReports.length} form(s) with reason: "${reason}"?`)
    if (!confirmed) return

    setLoading(true)

    try {
      // Group reports by template ID
      const reportsByTemplate = {}
      selectedReports.forEach(report => {
        const reportTemplateId = report.formTemplateId || report.templateId || report._templateId || templateId || import.meta.env.VITE_FUEL_LOG_TEMPLATE_ID
        if (!reportsByTemplate[reportTemplateId]) {
          reportsByTemplate[reportTemplateId] = []
        }
        reportsByTemplate[reportTemplateId].push(report.id)
      })

      // Process each template group
      let totalSuccess = 0
      let totalFailure = 0

      for (const [reportTemplateId, reportIds] of Object.entries(reportsByTemplate)) {
        if (!reportTemplateId) {
          console.error('Missing template ID for reports:', reportIds)
          totalFailure += reportIds.length
          continue
        }

        try {
          const result = await bulkRejectFieldReports(
            projectId,
            reportIds,
            reportTemplateId,
            {
              reason: reason.trim()
            }
          )

          const successCount = result.successCount ?? (result.results ? result.results.filter(r => r.success).length : 0)
          const failureCount = result.failureCount ?? (result.results ? result.results.filter(r => !r.success).length : 0)
          totalSuccess += successCount
          totalFailure += failureCount
        } catch (err) {
          console.error(`Bulk reject failed for template ${reportTemplateId}:`, err)
          totalFailure += reportIds.length
        }
      }

      onSuccess(`Bulk rejection finished. Success: ${totalSuccess} — Failed: ${totalFailure}`, 'reject')

      // Refresh the list if any succeeded
      if (totalSuccess > 0) {
        setTimeout(() => {
          onRefresh()
        }, 1500)
      }

    } catch (err) {
      const { errorCode, errorMsg } = extractErrorDetails(err)
      let displayMsg = `Bulk rejection failed: ${errorMsg}`
      if (errorCode) {
        displayMsg = `${displayMsg} (${errorCode})`
      }
      onError(displayMsg)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Save edited fuel log report
   */
  const saveFuelLogEdit = async (report, editedValues, FIELD_IDS) => {
    const id = report.id
    setRowError(prev => ({ ...prev, [id]: null }))
    setRowSuccess(prev => ({ ...prev, [id]: null }))
    setRowLoading(prev => ({ ...prev, [id]: true }))
    setRowAction(prev => ({ ...prev, [id]: 'save' }))

    const reportTemplateId = report.formTemplateId || report.templateId || templateId || import.meta.env.VITE_FUEL_LOG_TEMPLATE_ID

    try {
      // Build the customValues array with updated values
      const originalCustomValues = report.nativeForm?.customValues || []

      // Get template config to determine field types for fields not in originalCustomValues
      const templateConfig = TEMPLATE_FIELD_CONFIGS[reportTemplateId]

      // Build updated customValues array - ONLY include fields that changed
      const customValues = []

      FIELD_IDS.forEach(fieldId => {
        const value = editedValues[fieldId]
        const originalField = originalCustomValues.find(cv => (cv.fieldId || cv.id) === fieldId)

        // Determine value type based on original field or hardcoded field IDs (for backward compatibility)
        let valueType = 'text' // default
        let originalValue = null

        if (originalField) {
          if (originalField.dateVal !== undefined) {
            valueType = 'date'
            originalValue = originalField.dateVal
          } else if (originalField.numberVal !== undefined || originalField.numericVal !== undefined) {
            valueType = 'number'
            originalValue = originalField.numberVal || originalField.numericVal
          } else if (originalField.choiceVal !== undefined) {
            valueType = 'choice'
            originalValue = originalField.choiceVal
          } else if (originalField.boolVal !== undefined) {
            valueType = 'bool'
            originalValue = originalField.boolVal
          } else if (originalField.toggleVal !== undefined) {
            valueType = 'toggle'
            // For toggle fields, convert original string value to numeric for comparison
            if (originalField.toggleVal === 'True' || originalField.toggleVal === true) {
              originalValue = 1
            } else if (originalField.toggleVal === 'False' || originalField.toggleVal === false) {
              originalValue = 0
            } else if (originalField.toggleVal === 'NA') {
              originalValue = 2
            } else {
              originalValue = originalField.toggleVal
            }
          } else {
            valueType = 'text'
            originalValue = originalField.textVal
          }
        } else {
          // No original field - try to get value type from template field definitions
          if (templateFieldDefs && templateFieldDefs[reportTemplateId]) {
            const templateField = templateFieldDefs[reportTemplateId].fields?.find(f => f.fieldId === fieldId)
            if (templateField) {
              valueType = templateField.valueType || 'text'
            }
          }

          // Additional fallback: hardcoded field ID checks for backward compatibility
          if (valueType === 'text') {
            if (fieldId === 'a168234f-74c2-477c-95e3-f2a5e00f8694') {
              valueType = 'date'
            } else if (fieldId === 'b4a87b81-62e2-45f1-bc37-10a9d35f66e3') {
              valueType = 'number'
            }
          }
        }

        // Check if the value actually changed
        // For NEW fields (originalField is undefined), any non-empty value is a change
        const newValue = value !== undefined && value !== null && value !== '—' && value !== '' ? value : null

        let hasChanged = false
        if (!originalField && newValue !== null) {
          // Field doesn't exist in original form, but user entered a value
          hasChanged = true
        } else if (originalField) {
          // Field exists, compare old vs new
          if (valueType === 'toggle') {
            hasChanged = Number(originalValue) !== Number(newValue)
          } else {
            hasChanged = String(originalValue) !== String(newValue)
          }
        }

        // Only include this field if it changed
        if (!hasChanged) {
          return // Skip unchanged fields
        }

        const fieldUpdate = {
          fieldId: fieldId
        }

        // Set the appropriate value type
        if (newValue !== null) {
          if (valueType === 'date') {
            fieldUpdate.dateVal = newValue
          } else if (valueType === 'number') {
            fieldUpdate.numberVal = parseFloat(newValue) || 0
          } else if (valueType === 'choice') {
            fieldUpdate.choiceVal = newValue
          } else if (valueType === 'bool') {
            fieldUpdate.boolVal = Boolean(newValue)
          } else if (valueType === 'toggle') {
            // ACC expects toggleVal as a string: "True", "False", or "NA"
            const numValue = parseInt(newValue, 10)
            if (numValue === 1) {
              fieldUpdate.toggleVal = 'True'
            } else if (numValue === 0) {
              fieldUpdate.toggleVal = 'False'
            } else if (numValue === 2) {
              fieldUpdate.toggleVal = 'NA'
            } else {
              fieldUpdate.toggleVal = 'False' // default
            }
          } else {
            fieldUpdate.textVal = newValue
          }
        } else {
          // Clear the value based on type
          if (valueType === 'date') {
            fieldUpdate.dateVal = null
          } else if (valueType === 'number') {
            fieldUpdate.numberVal = null
          } else if (valueType === 'choice') {
            fieldUpdate.choiceVal = ''
          } else if (valueType === 'bool') {
            fieldUpdate.boolVal = false
          } else if (valueType === 'toggle') {
            fieldUpdate.toggleVal = 'False'
          } else {
            fieldUpdate.textVal = ''
          }
        }

        customValues.push(fieldUpdate)
      })

      // Check if location changed
      const originalLocationId = report.locationId || null
      const newLocationId = editedValues.locationId || report.locationId || null
      const locationChanged = String(newLocationId || '') !== String(originalLocationId || '')

      // Check if there are any changes (either field values or location)
      if (customValues.length === 0 && !locationChanged) {
        setRowSuccess(prev => ({ ...prev, [id]: 'No changes' }))
        onSuccess(`No changes to save for form ${report.formNum || id}.`)
        setRowLoading(prev => ({ ...prev, [id]: false }))
        return true // Not an error, just no changes
      }

      // ACC API has a limit of 10 fields per update
      if (customValues.length > 10) {
        console.warn(`[saveFuelLogEdit] Warning: Trying to update ${customValues.length} fields, but ACC API limit is 10. Only first 10 will be sent.`)
        customValues.splice(10) // Keep only first 10 fields
      }

      // Call update API
      await updateFieldReport(projectId, id, {
        customValues,
        locationId: editedValues.locationId || report.locationId || null,
        templateId: reportTemplateId
      })

      setRowSuccess(prev => ({ ...prev, [id]: 'Saved' }))
      onSuccess(`Form ${report.formNum || id} updated successfully.`)

      // Refresh the list
      setTimeout(() => {
        onRefresh()
      }, 1500)

      return true // Success

    } catch (err) {
      const { displayMsg } = extractErrorDetails(err)
      setRowError(prev => ({ ...prev, [id]: displayMsg }))
      onError(displayMsg)
      return false // Failure
    } finally {
      setRowLoading(prev => ({ ...prev, [id]: false }))
    }
  }

  return {
    approveFuelLog,
    rejectFuelLog,
    bulkApproveFuelLogs,
    bulkRejectFuelLogs,
    saveFuelLogEdit
  }
}
